import { useLazyMountWhenVisible } from '@/hooks/use-lazy-mount-when-visible';
import type { Editor } from '@tiptap/react';
import { useState, type RefObject } from 'react';

const toolIframeAllow = `clipboard-write ${import.meta.env.VITE_ZYBOOKS_URL}; clipboard-read ${import.meta.env.VITE_ZYBOOKS_URL}`;

type UseLazyMountIframeOptions = {
    containerRef: RefObject<Element | null>;
    editor: Editor;
    forceMount?: boolean;
    id: unknown;
};

type LazyMountIframeProps = {
    containerRef: RefObject<HTMLDivElement | null>;
    shouldMount: boolean;
    id: string;
    url: string;
    title: string;
    iframeRef: RefObject<HTMLIFrameElement | null>;
    loadingMessage: string;
    isIframeLoaded: boolean;
    onLoad: () => void;
    iframeClassName?: string;
};

export function useLazyMountIframe({ containerRef, editor, forceMount = false, id }: UseLazyMountIframeOptions) {
    const shouldMount = useLazyMountWhenVisible({
        containerRef,
        editor,
        forceMount,
    });

    const [loadedForId, setLoadedForId] = useState<string | null>(null);
    const [prevId, setPrevId] = useState(id);

    if (id !== prevId) {
        setPrevId(id);
        setLoadedForId(null);
    }

    const isIframeLoaded = loadedForId === String(id);

    return {
        shouldMount,
        isIframeLoaded,
        handleIframeLoad: () => setLoadedForId(String(id)),
        resetLoadState: () => setLoadedForId(null),
    };
}

function LazyMountLoadingPlaceholder({ message }: { message: string }) {
    return (
        <div
            className="flex h-full w-full items-center justify-center bg-zybooks-bg text-gray-500 dark:text-gray-400"
            aria-hidden
        >
            {message}
        </div>
    );
}

export function LazyMountIframe({
    containerRef,
    shouldMount,
    id,
    url,
    title,
    iframeRef,
    loadingMessage,
    isIframeLoaded,
    onLoad,
    iframeClassName = 'h-full w-full border-0',
}: LazyMountIframeProps) {
    return (
        <div
            ref={containerRef}
            className="h-full w-full"
        >
            {shouldMount ? (
                <div className="relative h-full w-full">
                    {!isIframeLoaded && (
                        <div
                            className="absolute inset-0 z-10 flex items-center justify-center bg-zybooks-bg text-gray-500 dark:text-gray-400"
                            aria-hidden={isIframeLoaded}
                        >
                            {loadingMessage}
                        </div>
                    )}
                    <iframe
                        ref={iframeRef}
                        key={id}
                        src={url}
                        className={iframeClassName}
                        title={title}
                        allow={toolIframeAllow}
                        onLoad={onLoad}
                    />
                </div>
            ) : (
                <LazyMountLoadingPlaceholder message={loadingMessage} />
            )}
        </div>
    );
}
